import { z } from 'zod'
import { DashboardCatalogView, dashboardCategoryFilterSchema } from './types'
import type {
  DashboardAssignItemCategoryPayload,
  DashboardAssignItemCustomizationGroupsPayload,
  DashboardCatalogCategoryResource,
  DashboardCategoryFilter,
  DashboardCategoryOptionResource,
  DashboardCatalogItemResource,
  DashboardCatalogModifierResource,
  DashboardItemOptionsResource,
  DashboardModifierItemOptionResource,
  DashboardModifierOptionResource,
  DashboardUpdateItemStatusPayload
} from './types'
import type {
  DashboardCatalogPaginatedData,
  DashboardCatalogRequestPresentation
} from './interfaces'
import type { UpsertMenuCustomizationGroupPayload } from '../menus/customization-groups/types'
import * as operations from '~/services/api/generated'
import { extendSchemaField } from '~/utils/extendSchemaField'
import { unboundedValueCodec } from '~/utils/unboundedValueCodec'
import { createDeleteOperation } from '~/services/api/createDeleteOperation'
import { createRequestOperation } from '~/utils/createRequestOperation'
import { buildMenuItemUpdateFormData } from '~/utils/buildMenuItemUpdateFormData'
import { strictify } from '~/utils/strictify'
import { MenuCategoryAssign } from '~/services/api/contracts/requests/MenuCategoryAssign'
import { MenuCategoryPartnerIndex } from '~/services/api/contracts/requests/MenuCategoryPartnerIndex'
import { MenuCategoryPartnerOptions } from '~/services/api/contracts/requests/MenuCategoryPartnerOptions'
import { MenuCategoryStoreForPartner } from '~/services/api/contracts/requests/MenuCategoryStoreForPartner'
import { MenuCategoryUpdateForPartner } from '~/services/api/contracts/requests/MenuCategoryUpdateForPartner'
import { MenuCustomizationGroupPartnerIndex } from '~/services/api/contracts/requests/MenuCustomizationGroupPartnerIndex'
import { MenuCustomizationGroupPartnerItemOptions } from '~/services/api/contracts/requests/MenuCustomizationGroupPartnerItemOptions'
import { MenuCustomizationGroupPartnerOptions } from '~/services/api/contracts/requests/MenuCustomizationGroupPartnerOptions'
import { MenuItemAssignCategory } from '~/services/api/contracts/requests/MenuItemAssignCategory'
import { MenuItemAssignCustomizationGroups } from '~/services/api/contracts/requests/MenuItemAssignCustomizationGroups'
import { MenuItemPartnerIndex } from '~/services/api/contracts/requests/MenuItemPartnerIndex'
import { MenuItemStoreForPartner } from '~/services/api/contracts/requests/MenuItemStoreForPartner'
import { MenuItemUpdateByItem } from '~/services/api/contracts/requests/MenuItemUpdateByItem'
import { MenuItemUpdateStatusByItem } from '~/services/api/contracts/requests/MenuItemUpdateStatusByItem'
import { MenuMenus } from '~/services/api/contracts/requests/MenuMenus'
import { MenuCustomizationGroupStoreForPartner } from '~/services/api/contracts/requests/MenuCustomizationGroupStoreForPartner'
import { MenuCustomizationGroupUpdate } from '~/services/api/contracts/requests/MenuCustomizationGroupUpdate'
import { MenuCustomizationGroupReplaceLinkedItems } from '~/services/api/contracts/requests/MenuCustomizationGroupReplaceLinkedItems'

const unboundedSelectionLimitExtension = {
  max_per_option: unboundedValueCodec('Infinity'),
  max_total_selections: unboundedValueCodec('Infinity')
}
const menuCustomizationGroupPartnerIndexSuccessCodecSchema = extendSchemaField(
  operations.MenuCustomizationGroupPartnerIndexSuccessSchema,
  ['data', 'items'],
  unboundedSelectionLimitExtension
)
const menuCustomizationGroupStoreForPartnerSuccessCodecSchema = extendSchemaField(
  operations.MenuCustomizationGroupStoreForPartnerSuccessSchema,
  ['data'],
  unboundedSelectionLimitExtension
)
const menuCustomizationGroupUpdateByModifierSuccessCodecSchema = extendSchemaField(
  operations.MenuCustomizationGroupUpdateByModifierSuccessSchema,
  ['data'],
  unboundedSelectionLimitExtension
)
const menuCustomizationGroupDuplicateByModifierSuccessCodecSchema = extendSchemaField(
  operations.MenuCustomizationGroupDuplicateByModifierSuccessSchema,
  ['data'],
  unboundedSelectionLimitExtension
)
const menuCustomizationGroupReplaceLinkedItemsSuccessCodecSchema = extendSchemaField(
  operations.MenuCustomizationGroupReplaceLinkedItemsSuccessSchema,
  ['data'],
  unboundedSelectionLimitExtension
)

const formBoolean = (value: boolean): '1' | '0' => (value ? '1' : '0')

const viewOf =
  <Dto, Resource>(view: (dto: Dto) => Resource) =>
  (response: { readonly data: Dto }): Resource =>
    view(response.data)

const pageOf =
  <Dto, Resource, Meta>(view: (dto: Dto) => Resource) =>
  (response: {
    readonly data: { readonly items: readonly Dto[]; readonly pagination: Meta }
  }): DashboardCatalogPaginatedData<Resource, Meta> => ({
    data: response.data.items.map((dto) => view(dto)),
    meta: response.data.pagination
  })

export function useDashboardCatalogService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl}/api/dashboard`
  const sanctumClient = useApiRequest()
  const itemUrl = (itemId: number, suffix: string): string => `${baseURL}/items/${itemId}${suffix}`

  const deleteCategory = createDeleteOperation(
    sanctumClient,
    (categoryId) =>
      operations.MenuCategoryDestroyForPartnerPathSchema.strict().parse({ categoryId }),
    operations.MenuCategoryDestroyForPartnerSuccessSchema,
    ({ categoryId }) => `${baseURL}/categories/${categoryId}`
  )
  const deleteItem = createDeleteOperation(
    sanctumClient,
    (itemId) => operations.MenuItemDestroyByItemPathSchema.strict().parse({ itemId }),
    operations.MenuItemDestroyByItemSuccessSchema,
    ({ itemId }) => `${baseURL}/items/${itemId}`
  )
  const deleteModifier = createDeleteOperation(
    sanctumClient,
    (groupId) =>
      operations.MenuCustomizationGroupDestroyByModifierPathSchema.strict().parse({ groupId }),
    operations.MenuCustomizationGroupDestroyByModifierSuccessSchema,
    ({ groupId }) => `${baseURL}/modifiers/${groupId}`
  )

  const getMenus = createRequestOperation(
    sanctumClient,
    operations.MenuMenusSuccessSchema,
    (
      query: operations.MenuMenusQuery,
      { globalErrorUi = true }: DashboardCatalogRequestPresentation = {}
    ) => ({
      url: `${baseURL}/menus`,
      options: { globalErrorUi, query: MenuMenus({}).parse(query) }
    }),
    pageOf(DashboardCatalogView.menu)
  )

  const getCategories = createRequestOperation(
    sanctumClient,
    operations.MenuCategoryPartnerIndexSuccessSchema,
    (query: operations.MenuCategoryPartnerIndexQuery) => ({
      url: `${baseURL}/categories`,
      options: { globalErrorUi: true, query: MenuCategoryPartnerIndex({}).parse(query) }
    }),
    pageOf(DashboardCatalogView.category)
  )

  async function createCategory(
    payload: operations.MenuCategoryStoreForPartnerRequest
  ): Promise<DashboardCatalogCategoryResource> {
    const request = MenuCategoryStoreForPartner({}).parse(payload)
    const response = operations.MenuCategoryStoreForPartnerSuccessSchema.parse(
      await sanctumClient(`${baseURL}/categories`, {
        method: 'POST',
        body: request
      })
    )

    return DashboardCatalogView.category(response.data)
  }

  async function getCategory(
    categoryId: operations.MenuCategoryPartnerDetailPath['categoryId']
  ): Promise<DashboardCatalogCategoryResource> {
    const path = operations.MenuCategoryPartnerDetailPathSchema.strict().parse({ categoryId })
    const response = operations.MenuCategoryPartnerDetailSuccessSchema.parse(
      await sanctumClient(`${baseURL}/categories/${path.categoryId}/detail`, {
        globalErrorUi: true
      })
    )
    return DashboardCatalogView.category(response.data)
  }

  async function updateCategory(
    categoryId: operations.MenuCategoryUpdateForPartnerPath['categoryId'],
    payload: operations.MenuCategoryUpdateForPartnerRequest
  ): Promise<DashboardCatalogCategoryResource> {
    const path = operations.MenuCategoryUpdateForPartnerPathSchema.strict().parse({ categoryId })
    const request = MenuCategoryUpdateForPartner({}).parse(payload)
    const response = operations.MenuCategoryUpdateForPartnerSuccessSchema.parse(
      await sanctumClient(`${baseURL}/categories/${path.categoryId}`, {
        body: request,
        method: 'PUT'
      })
    )
    return DashboardCatalogView.category(response.data)
  }

  async function getCategoryFilters(): Promise<DashboardCategoryFilter[]> {
    const response = operations.MenuCategoryFiltersSuccessSchema.parse(
      await sanctumClient(`${baseURL}/categories/filters`)
    )

    return response.data.map((item) => dashboardCategoryFilterSchema.parse(item))
  }

  async function getCategoryOptions(
    query: operations.MenuCategoryPartnerOptionsQuery
  ): Promise<DashboardCatalogPaginatedData<DashboardCategoryOptionResource>> {
    const request = MenuCategoryPartnerOptions({}).parse(query)
    const response = operations.MenuCategoryPartnerOptionsSuccessSchema.parse(
      await sanctumClient(`${baseURL}/categories/options`, { query: request })
    )
    return { data: response.data.items, meta: response.data.pagination }
  }

  const getItems = createRequestOperation(
    sanctumClient,
    operations.MenuItemPartnerIndexSuccessSchema,
    (query: operations.MenuItemPartnerIndexQuery) => ({
      url: `${baseURL}/items`,
      options: { globalErrorUi: true, query: MenuItemPartnerIndex({}).parse(query) }
    }),
    pageOf(DashboardCatalogView.item)
  )

  const getItem = createRequestOperation(
    sanctumClient,
    operations.MenuItemShowSuccessSchema,
    (itemId: operations.MenuItemShowPath['itemId']) => ({
      url: itemUrl(operations.MenuItemShowPathSchema.strict().parse({ itemId }).itemId, ''),
      options: { globalErrorUi: true }
    }),
    viewOf(DashboardCatalogView.item)
  )

  async function getCreateItemOptions(
    query: operations.MenuItemPartnerCreateOptionsQuery = {}
  ): Promise<DashboardItemOptionsResource> {
    const request = operations.MenuItemPartnerCreateOptionsQuerySchema.strict().parse(query)
    const response = strictify(operations.MenuItemPartnerCreateOptionsSuccessSchema).parse(
      await sanctumClient(`${baseURL}/items/options`, { query: request })
    )
    return DashboardCatalogView.itemOptions(response.data)
  }

  async function getItemOptions(
    itemId: operations.MenuItemItemOptionsPath['itemId']
  ): Promise<DashboardItemOptionsResource> {
    const path = operations.MenuItemItemOptionsPathSchema.strict().parse({ itemId })
    const response = operations.MenuItemItemOptionsSuccessSchema.parse(
      await sanctumClient(`${baseURL}/items/${path.itemId}/options`)
    )
    return DashboardCatalogView.itemOptions(response.data)
  }

  async function createItem(
    payload: z.output<ReturnType<typeof MenuItemStoreForPartner>>
  ): Promise<DashboardCatalogItemResource> {
    const request = MenuItemStoreForPartner({}).parse(payload)
    const formData = new FormData()

    if (request.customization_group_ids !== undefined) {
      for (const groupId of request.customization_group_ids) {
        formData.append('customization_group_ids[]', String(groupId))
      }
    }
    if (request.customization_groups !== undefined) {
      for (const [index, group] of request.customization_groups.entries()) {
        formData.append(`customization_groups[${index}][id]`, String(group.id))
        if (group.sort_order !== undefined) {
          formData.append(`customization_groups[${index}][sort_order]`, String(group.sort_order))
        }
      }
    }
    formData.append('default_price', String(request.default_price))
    if (request.description !== undefined) {
      formData.append('description', request.description ?? '')
    }
    if (request.dietary_attribute_ids !== undefined) {
      for (const attributeId of request.dietary_attribute_ids) {
        formData.append('dietary_attribute_ids[]', String(attributeId))
      }
    }
    if (request.individual_packaging_available !== undefined) {
      formData.append(
        'individual_packaging_available',
        formBoolean(request.individual_packaging_available)
      )
    }
    if (request.is_available !== undefined) {
      formData.append('is_available', formBoolean(request.is_available))
    }
    if (request.is_featured !== undefined) {
      formData.append('is_featured', formBoolean(request.is_featured))
    }
    if (request.is_package !== undefined) {
      formData.append('is_package', formBoolean(request.is_package))
    }
    if (request.menu_category_id !== undefined) {
      formData.append('menu_category_id', String(request.menu_category_id ?? ''))
    }
    if (request.menu_id !== undefined) {
      formData.append('menu_id', String(request.menu_id))
    }
    if (request.minimum_guest_count !== undefined) {
      formData.append('minimum_guest_count', String(request.minimum_guest_count ?? ''))
    }
    formData.append('name', request.name)
    if (request.out_of_stock !== undefined) {
      formData.append('out_of_stock', formBoolean(request.out_of_stock))
    }
    if (request.photo !== undefined) {
      formData.append('photo', request.photo)
    }
    formData.append('price_type', request.price_type)
    if (request.serves_count !== undefined) {
      formData.append('serves_count', request.serves_count ?? '')
    }
    if (request.sort_order !== undefined) {
      formData.append('sort_order', String(request.sort_order))
    }
    if (request.status !== undefined) {
      formData.append('status', request.status)
    }
    formData.append('tax_category', request.tax_category)
    if (request.weight_unit !== undefined) {
      formData.append('weight_unit', request.weight_unit ?? '')
    }
    if (request.weight_value !== undefined) {
      formData.append('weight_value', String(request.weight_value ?? ''))
    }

    const response = operations.MenuItemStoreForPartnerSuccessSchema.parse(
      await sanctumClient(`${baseURL}/items`, {
        method: 'POST',
        body: formData
      })
    )
    return DashboardCatalogView.item(response.data)
  }

  const updateItem = createRequestOperation(
    sanctumClient,
    operations.MenuItemUpdateByItemSuccessSchema,
    (
      itemId: operations.MenuItemUpdateByItemPath['itemId'],
      payload: z.output<ReturnType<typeof MenuItemUpdateByItem>>
    ) => ({
      url: itemUrl(operations.MenuItemUpdateByItemPathSchema.strict().parse({ itemId }).itemId, ''),
      options: {
        method: 'PUT',
        body: buildMenuItemUpdateFormData(MenuItemUpdateByItem({}).parse(payload))
      }
    }),
    viewOf(DashboardCatalogView.item)
  )

  const updateItemStatus = createRequestOperation(
    sanctumClient,
    operations.MenuItemUpdateStatusByItemSuccessSchema,
    (
      itemId: operations.MenuItemUpdateStatusByItemPath['itemId'],
      payload: DashboardUpdateItemStatusPayload
    ) => ({
      url: itemUrl(
        operations.MenuItemUpdateStatusByItemPathSchema.strict().parse({ itemId }).itemId,
        '/status'
      ),
      options: { method: 'PUT', body: MenuItemUpdateStatusByItem({}).parse(payload) }
    }),
    viewOf(DashboardCatalogView.item)
  )

  const assignItemCategory = createRequestOperation(
    sanctumClient,
    operations.MenuItemAssignCategorySuccessSchema,
    (
      itemId: operations.MenuItemAssignCategoryPath['itemId'],
      payload: DashboardAssignItemCategoryPayload
    ) => ({
      url: itemUrl(
        operations.MenuItemAssignCategoryPathSchema.strict().parse({ itemId }).itemId,
        '/assign-category'
      ),
      options: { method: 'PUT', body: MenuItemAssignCategory({}).parse(payload) }
    }),
    viewOf(DashboardCatalogView.item)
  )

  const assignItemCustomizationGroups = createRequestOperation(
    sanctumClient,
    operations.MenuItemAssignCustomizationGroupsSuccessSchema,
    (
      itemId: operations.MenuItemAssignCustomizationGroupsPath['itemId'],
      payload: DashboardAssignItemCustomizationGroupsPayload
    ) => ({
      url: itemUrl(
        operations.MenuItemAssignCustomizationGroupsPathSchema.strict().parse({ itemId }).itemId,
        '/assign-customization-groups'
      ),
      options: { method: 'PUT', body: MenuItemAssignCustomizationGroups({}).parse(payload) }
    }),
    viewOf(DashboardCatalogView.item)
  )

  async function assignCategoryToMenu(
    menuId: operations.MenuCategoryAssignPath['menuId'],
    categoryId: operations.MenuCategoryAssignPath['categoryId']
  ): Promise<DashboardCatalogCategoryResource> {
    const path = operations.MenuCategoryAssignPathSchema.strict().parse({ categoryId, menuId })
    const request = MenuCategoryAssign({}).parse({})
    const response = operations.MenuCategoryAssignSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/categories/${path.categoryId}/assign`, {
        method: 'POST',
        body: request
      })
    )

    const categories = z.array(operations.MenuCategoryManagementDtoSchema).parse(response.data)
    const category = categories.find((candidate) => candidate.id === path.categoryId)
    if (category === undefined) {
      throw new TypeError(
        'Invalid POST /dashboard/menus/:menuId/categories/:categoryId/assign response: assigned category is missing.'
      )
    }
    return DashboardCatalogView.category(category)
  }

  async function unassignCategoryFromMenu(
    menuId: operations.MenuCategoryUnassignPath['menuId'],
    categoryId: operations.MenuCategoryUnassignPath['categoryId']
  ): Promise<operations.MenuCategoryUnassignSuccess> {
    const path = operations.MenuCategoryUnassignPathSchema.strict().parse({
      categoryId,
      menuId
    })
    return operations.MenuCategoryUnassignSuccessSchema.parse(
      await sanctumClient(
        `${baseURL}/menus/${path.menuId}/categories/${path.categoryId}/unassign`,
        {
          method: 'DELETE'
        }
      )
    )
  }

  const getModifiers = createRequestOperation(
    sanctumClient,
    menuCustomizationGroupPartnerIndexSuccessCodecSchema,
    (query: operations.MenuCustomizationGroupPartnerIndexQuery) => ({
      url: `${baseURL}/modifiers`,
      options: {
        globalErrorUi: true,
        query: MenuCustomizationGroupPartnerIndex({}).parse(query)
      }
    }),
    pageOf(DashboardCatalogView.modifier)
  )

  async function getModifierItemOptions(
    query: operations.MenuCustomizationGroupPartnerItemOptionsQuery
  ): Promise<DashboardCatalogPaginatedData<DashboardModifierItemOptionResource>> {
    const request = MenuCustomizationGroupPartnerItemOptions({}).parse(query)
    const response = operations.MenuCustomizationGroupPartnerItemOptionsSuccessSchema.parse(
      await sanctumClient(`${baseURL}/modifiers/options/items`, {
        query: request
      })
    )

    return {
      data: response.data.items.map(DashboardCatalogView.modifierItemOption),
      meta: response.data.pagination
    }
  }

  async function getModifierOptions(
    query: operations.MenuCustomizationGroupPartnerOptionsQuery
  ): Promise<DashboardCatalogPaginatedData<DashboardModifierOptionResource>> {
    const request = MenuCustomizationGroupPartnerOptions({}).parse(query)
    const response = operations.MenuCustomizationGroupPartnerOptionsSuccessSchema.parse(
      await sanctumClient(`${baseURL}/modifiers/options`, { query: request })
    )

    return {
      data: response.data.items.map(DashboardCatalogView.modifierOption),
      meta: response.data.pagination
    }
  }

  async function createModifier(
    payload: UpsertMenuCustomizationGroupPayload
  ): Promise<DashboardCatalogModifierResource> {
    const { is_required: _isRequired, options, ...fields } = payload
    const request = MenuCustomizationGroupStoreForPartner({}).parse({
      ...fields,
      options: options.map(({ id: _id, ...option }) => option)
    })
    const response = menuCustomizationGroupStoreForPartnerSuccessCodecSchema.parse(
      await sanctumClient(`${baseURL}/modifiers`, {
        method: 'POST',
        body: request
      })
    )
    return DashboardCatalogView.modifier(response.data)
  }

  async function updateModifier(
    modifierId: operations.MenuCustomizationGroupUpdateByModifierPath['groupId'],
    payload: UpsertMenuCustomizationGroupPayload
  ): Promise<DashboardCatalogModifierResource> {
    const path = operations.MenuCustomizationGroupUpdateByModifierPathSchema.strict().parse({
      groupId: modifierId
    })
    const { is_required: _isRequired, ...generatedPayload } = payload
    const request = MenuCustomizationGroupUpdate({}).parse(generatedPayload)
    const response = menuCustomizationGroupUpdateByModifierSuccessCodecSchema.parse(
      await sanctumClient(`${baseURL}/modifiers/${path.groupId}`, {
        method: 'PUT',
        body: request
      })
    )
    return DashboardCatalogView.modifier(response.data)
  }

  const replaceLinkedItems = createRequestOperation(
    sanctumClient,
    strictify(menuCustomizationGroupReplaceLinkedItemsSuccessCodecSchema),
    (
      modifierId: operations.MenuCustomizationGroupReplaceLinkedItemsPath['groupId'],
      payload: operations.MenuCustomizationGroupReplaceLinkedItemsRequest
    ) => ({
      url: `${baseURL}/modifiers/${
        operations.MenuCustomizationGroupReplaceLinkedItemsPathSchema.strict().parse({
          groupId: modifierId
        }).groupId
      }/linked-items`,
      options: {
        method: 'PUT',
        body: MenuCustomizationGroupReplaceLinkedItems({}).parse(payload)
      }
    }),
    viewOf(DashboardCatalogView.modifier)
  )

  async function duplicateModifier(
    modifierId: operations.MenuCustomizationGroupDuplicateByModifierPath['groupId']
  ): Promise<DashboardCatalogModifierResource> {
    const path = operations.MenuCustomizationGroupDuplicateByModifierPathSchema.strict().parse({
      groupId: modifierId
    })
    const response = menuCustomizationGroupDuplicateByModifierSuccessCodecSchema.parse(
      await sanctumClient(`${baseURL}/modifiers/${path.groupId}/duplicate`, {
        method: 'POST'
      })
    )
    return DashboardCatalogView.modifier(response.data)
  }

  return {
    getCategories,
    getCategory,
    createCategory,
    updateCategory,
    deleteCategory,
    getCategoryFilters,
    getCategoryOptions,
    getMenus,
    getItem,
    getCreateItemOptions,
    getItemOptions,
    getItems,
    createItem,
    updateItem,
    updateItemStatus,
    assignItemCategory,
    assignItemCustomizationGroups,
    deleteItem,
    assignCategoryToMenu,
    unassignCategoryFromMenu,
    getModifiers,
    getModifierItemOptions,
    getModifierOptions,
    createModifier,
    updateModifier,
    replaceLinkedItems,
    deleteModifier,
    duplicateModifier
  }
}
